/**
 * Editor dei recapiti email di un utente.
 *
 * Un solo indirizzo e' "principale": identifica l'account ed e' l'unico
 * destinatario del link di recupero password. Gli altri servono soltanto alla
 * consegna (PDF piano turni, bacheca/notifiche) e possono essere disattivati
 * canale per canale — anche il principale, per chi vuole tenere separato
 * l'indirizzo dell'account da quello su cui riceve i turni.
 *
 * Usato sia dalla pagina Utenti (admin, su qualunque utente) sia da
 * "Impostazioni account" (l'utente sui propri recapiti).
 */

export const MAX_USER_EMAILS = 5;

function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

let rowSeq = 0;

function rowHtml(entry, groupName) {
  const id = `ue-${++rowSeq}`;
  return `
    <div class="ue-row" style="border:1px solid var(--border);border-radius:6px;padding:0.5rem;margin-bottom:0.5rem;">
      <div style="display:flex;gap:0.5rem;align-items:center;">
        <input type="email" class="ue-email" value="${escapeHtml(entry.email || '')}" placeholder="nome@esempio.it"
               autocomplete="email" style="flex:1;min-width:0;padding:0.4rem;">
        <button type="button" class="btn btn-secondary btn-sm ue-remove" title="Rimuovi recapito" aria-label="Rimuovi recapito">×</button>
      </div>
      <div style="display:flex;gap:0.9rem;flex-wrap:wrap;margin-top:0.4rem;font-size:0.85rem;color:var(--text-secondary);">
        <label style="display:flex;gap:0.25rem;align-items:center;">
          <input type="radio" name="${escapeHtml(groupName)}" class="ue-primary" id="${id}-p" ${entry.isPrimary ? 'checked' : ''}>
          Principale
        </label>
        <label style="display:flex;gap:0.25rem;align-items:center;">
          <input type="checkbox" class="ue-pdf" ${entry.recvShiftPdf === false ? '' : 'checked'}> PDF turni
        </label>
        <label style="display:flex;gap:0.25rem;align-items:center;">
          <input type="checkbox" class="ue-board" ${entry.recvBoard === false ? '' : 'checked'}> Bacheca e notifiche
        </label>
      </div>
    </div>
  `;
}

/**
 * Monta l'editor dentro `host`.
 * @param {HTMLElement} host
 * @param {Array<{email:string,isPrimary?:boolean,recvShiftPdf?:boolean,recvBoard?:boolean}>} emails
 */
export function renderEmailRecipientsEditor(host, emails) {
  if (!host) return;
  const groupName = `ue-primary-${++rowSeq}`;
  const list = Array.isArray(emails) ? emails.filter((e) => e && String(e.email || '').trim()) : [];
  if (list.length === 0) list.push({ email: '', isPrimary: true, recvShiftPdf: true, recvBoard: true });
  if (!list.some((e) => e.isPrimary)) list[0].isPrimary = true;

  host.innerHTML = `
    <div class="ue-list">${list.map((e) => rowHtml(e, groupName)).join('')}</div>
    <button type="button" class="btn btn-secondary btn-sm ue-add">+ Aggiungi recapito</button>
    <small style="display:block;margin-top:0.4rem;color:var(--text-secondary);">
      Il recapito <strong>principale</strong> identifica l'account ed e' l'unico a cui viene inviato il link di
      recupero password. Le caselle indicano cosa riceve ciascun indirizzo. Massimo ${MAX_USER_EMAILS} recapiti.
    </small>
  `;
  host.dataset.ueGroup = groupName;

  const listEl = host.querySelector('.ue-list');
  const addBtn = host.querySelector('.ue-add');

  const syncState = () => {
    const rows = [...listEl.querySelectorAll('.ue-row')];
    // Con un solo recapito il "rimuovi" svuoterebbe l'elenco: meglio disabilitarlo.
    rows.forEach((r) => {
      const btn = r.querySelector('.ue-remove');
      if (btn) btn.disabled = rows.length <= 1;
    });
    if (rows.length && !rows.some((r) => r.querySelector('.ue-primary')?.checked)) {
      const first = rows[0].querySelector('.ue-primary');
      if (first) first.checked = true;
    }
    if (addBtn) addBtn.disabled = rows.length >= MAX_USER_EMAILS;
  };

  listEl.addEventListener('click', (e) => {
    const btn = e.target.closest('.ue-remove');
    if (!btn) return;
    btn.closest('.ue-row')?.remove();
    syncState();
  });

  addBtn?.addEventListener('click', () => {
    if (listEl.querySelectorAll('.ue-row').length >= MAX_USER_EMAILS) return;
    listEl.insertAdjacentHTML('beforeend', rowHtml({ email: '', recvShiftPdf: true, recvBoard: true }, groupName));
    syncState();
    listEl.querySelector('.ue-row:last-child .ue-email')?.focus();
  });

  syncState();
}

/**
 * Legge i recapiti dall'editor.
 * @returns {Array<{email:string,isPrimary:boolean,recvShiftPdf:boolean,recvBoard:boolean}>}
 */
export function collectEmailRecipients(host) {
  if (!host) return [];
  return [...host.querySelectorAll('.ue-row')]
    .map((row) => ({
      email: String(row.querySelector('.ue-email')?.value || '').trim(),
      isPrimary: !!row.querySelector('.ue-primary')?.checked,
      recvShiftPdf: !!row.querySelector('.ue-pdf')?.checked,
      recvBoard: !!row.querySelector('.ue-board')?.checked,
    }))
    .filter((e) => e.email);
}

/**
 * Controlli lato client, per dare l'errore prima della chiamata di rete.
 * @returns {string|null} messaggio d'errore, null se va bene
 */
export function validateEmailRecipients(items) {
  const list = items || [];
  if (list.length === 0) return null; // nessun recapito: ammesso
  if (list.length > MAX_USER_EMAILS) return `Massimo ${MAX_USER_EMAILS} recapiti per utente.`;
  const seen = new Set();
  for (const e of list) {
    if (!/^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(e.email)) return `Email non valida: ${e.email}`;
    const key = e.email.toLowerCase();
    if (seen.has(key)) return `Indirizzo ripetuto: ${e.email}`;
    seen.add(key);
  }
  if (list.filter((e) => e.isPrimary).length !== 1) return 'Indicare esattamente un recapito principale.';
  return null;
}

/** Riepilogo compatto per le tabelle: principale + "+N". */
export function summarizeEmails(user) {
  const list = Array.isArray(user?.emails) ? user.emails : [];
  if (list.length === 0) {
    const legacy = String(user?.email || '').trim();
    return legacy ? escapeHtml(legacy) : '—';
  }
  const primary = list.find((e) => e.isPrimary) || list[0];
  const extra = list.length - 1;
  const badge = extra > 0
    ? ` <span class="badge" style="background:var(--bg-secondary);color:var(--text-secondary);padding:0 0.35rem;border-radius:8px;font-size:0.75rem;" title="${escapeHtml(list.filter((e) => e !== primary).map((e) => e.email).join(', '))}">+${extra}</span>`
    : '';
  return `${escapeHtml(primary.email)}${badge}`;
}
